import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { connect } from 'react-redux';
import Global from '../lib/Global';
import AjaxActions from '../redux/AjaxRedux';
import EventReportsActions from '../redux/EventReportsRedux';
import SitesActions from '../redux/SitesRedux';
import WebSocketActions from '../redux/WebSocketRedux';
import { withStyles } from '@material-ui/styles';
import withWidth from '@material-ui/core/withWidth';
import clsx from 'clsx';
import { Colors, StyledComponents, Metrics } from '../themes';
import cloneDeep from 'lodash/cloneDeep';
import Header from '../components/header/Header';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Table from '../components/common/Table';
import AlertDialog from '../components/common/AlertDialog';
import SnackbarNotification from '../components/common/SnackbarNotification';
import { formatDateTime, checkPermission, getTimestamp, hackToSubtractOffset, hackToAddOffset } from '../lib/Utils';
import { availableSettings } from '../config/AppConfig';
import Modal from '../components/common/Modal';
import SelectSitesCheckAllModal from '../components/reportsElements/SelectSitesCheckAllModal';
import { format, subHours, endOfDay } from 'date-fns';
import Immutable from 'seamless-immutable';
import isEqual from 'lodash.isequal';

const { MainContentContainer } = StyledComponents;
const HOURS_BEFORE = 6; // number of hours to retrieve data

const styles = (theme) => ({
  gridContainer: {
    [theme.breakpoints.up('xl')]: {
      maxWidth: Metrics.contentGridMaxWidthXl.general,
    },
  },
  paper: {
    textAlign: 'center',
    background: Colors.hoverGrey,
    color: Colors.white,
  },
});

class Monitoring extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // snackbar
      isSnackbarNotificationOpen: false,
      snackbarVariant: 'warning',
      snackbarMessage: '',
      // alert confirm dialog
      isAlertDialogOpen: false,
      alertTitle: '',
      alertContentText: '',
      alertConfirmAction: null,
      // sites selection
      selectedSites: [],
      tempSelectedSites: [],
      isSelectAllSitesButtonChecked: true,
      // select sites modal
      isSitesModalOpen: false,
      selectAtLeastOneSiteError: false,
      // ajax
      hasAjax: false,
      eventReportData: [],
    };
  }

  componentDidMount() {
    if (Global.api.accessToken) {
      this.checkAjax();
      this.sendGetAllSitesRequest();
      this.getReportData();
    }
  }

  componentWillUnmount() {
    this.props.resetAjax();
    this.props.resetSites();
    this.props.clearWebSocketEvents();
    this.props.updateSitesQueues([]);
  }

  componentDidUpdate(prevProps, prevState) {
    const { customerId, userId, ajaxErrorMessage, loginAjax, hubs, getHubs, getLogs, includeAjax } = this.props;
    const { hasAjax } = this.state;
    if (hasAjax && includeAjax) {
      if (!userId && !ajaxErrorMessage && customerId && Global.api.accessToken) {
        loginAjax(customerId);
      }
      if (userId && userId !== prevProps.userId) {
        getHubs(customerId, userId);
      }
      if (hubs.length > 0 && hubs.length !== prevProps.hubs.length) {
        hubs.forEach((hub) => getLogs(customerId, userId, hub.hubId));
      }
    }
    if (prevProps.customerId !== this.props.customerId) {
      this.props.resetSites();
      this.checkAjax();
      if (this.props.customerId) {
        this.sendGetAllSitesRequest();
        this.sendGetEventReportRequest();
      }
    }
    if (prevProps.eventReportIsLoading && !this.props.eventReportIsLoading) {
      if (this.props.eventReportErrorMessage) {
        this.setState({
          isSnackbarNotificationOpen: true,
          snackbarVariant: 'error',
          snackbarMessage: this.props.eventReportErrorMessage,
        });
      }
    }
    // sites checkbox select all
    if (!prevState.isSelectAllSitesButtonChecked && this.state.isSelectAllSitesButtonChecked) {
      this.setState({ tempSelectedSites: this.props.sites });
    }
    if (
      prevState.isSelectAllSitesButtonChecked &&
      !this.state.isSelectAllSitesButtonChecked &&
      this.haveSameIds(this.props.sites, this.state.tempSelectedSites, 'siteId')
    ) {
      this.setState({ tempSelectedSites: [] });
    }
    if (prevState.tempSelectedSites !== this.state.tempSelectedSites) {
      this.setState({
        isSelectAllSitesButtonChecked: this.haveSameIds(this.props.sites, this.state.tempSelectedSites, 'siteId'),
      });
    }
    // select all sites initially and subscribe with webSocket
    if (!isEqual(prevProps.sites, this.props.sites)) {
      this.setState({ selectedSites: this.props.sites });
      this.props.updateSitesQueues(this.props.sites.map(({ siteId }) => siteId))
    }
  }

  getReportData = () => {
    this.sendGetEventReportRequest();
    if (this.state.hasAjax && this.props.includeAjax) {
      this.sendAjaxRequest();
    }
  };

  sendAjaxRequest = () => {
    const { customerId, userId, ajaxErrorMessage, loginAjax, hubs, getHubs, getLogs } = this.props;
    if (!userId && !ajaxErrorMessage && customerId && Global.api.accessToken) {
      loginAjax(customerId);
    }
    if (hubs.length > 0) {
      hubs.forEach((hub) => getLogs(customerId, userId, hub.hubId));
    }
  };

  sendGetAllSitesRequest = () => {
    const pageParams = {
      items: -1,
      page: 0,
      value: null,
      sort: 'ASC',
    };
    this.props.getAllSites(this.props.customerId, pageParams);
  };

  // Sites Modal methods
  haveSameIds = (biggerArray, smallerArray, idName) =>
    biggerArray?.length > 0 &&
    smallerArray?.length > 0 &&
    biggerArray?.every((element1) => smallerArray?.findIndex((element2) => element2[idName] === element1[idName]) > -1);

  selectSite = (site) => {
    const previousSites = this.state.tempSelectedSites;
    //check if the site was present in selectedSites
    const selectedIndex = previousSites?.findIndex((item) => item.siteId === site.siteId);
    let newSelectedSites = [];

    if (selectedIndex === -1) {
      // if not found, add it
      newSelectedSites = newSelectedSites.concat(previousSites, site);
    } else if (selectedIndex === 0) {
      // if first item, remove it
      newSelectedSites = newSelectedSites.concat(previousSites.slice(1));
    } else if (selectedIndex === previousSites?.length - 1) {
      // if last item, remove it
      newSelectedSites = newSelectedSites.concat(previousSites.slice(0, -1));
    } else if (selectedIndex > 0) {
      // if between first and last, remove it
      newSelectedSites = newSelectedSites.concat(
        previousSites.slice(0, selectedIndex),
        previousSites.slice(selectedIndex + 1),
        );
      }
    this.setState({
      tempSelectedSites: newSelectedSites,
      selectAtLeastOneSiteError: newSelectedSites.length < 1,
    });
  };

  closeModalWithoutSave = () => {
    this.setState((prevState) => {
      return {
      tempSelectedSites: prevState.selectedSites,
      isSitesModalOpen: false,
    }
  });
  };

  saveSelectedSites = () => {
    !this.state.selectAtLeastOneSiteError &&
      this.setState(
        (prevState) => ({
          selectedSites: prevState.tempSelectedSites,
          isSitesModalOpen: false,
        }),
        () => {
          this.getReportData();
          this.props.updateSitesQueues(this.state.selectedSites.map(({ siteId }) => siteId));
        },
      );
  };

  // eventReport list table methods

  sendGetEventReportRequest = () => {
    const { selectedSites } = this.state;
    const now = new Date(hackToAddOffset(new Date().toJSON()));
    const startTime = subHours(now, HOURS_BEFORE)
      .toJSON()
      .slice(0, -5);
    const endTime = hackToAddOffset(endOfDay(now).toJSON()).slice(0, -5);
    const filters = {
      sites: selectedSites.map(({ siteId }) => siteId),
    };
    const params = {
      startTime,
      endTime, // format '2021-06-13T20:59:59',
      items: -1,
      page: 0,
      value: null,
      sortBy: 'eventLogId',
      sortDirection: 'DESC',
    };
    this.props.getEventReport(this.props.customerId, filters, params);
  };

  checkAjax = () => {
    const { customers, customerIdSub } = this.props;
    const hasAjax = checkPermission(customers, customerIdSub, availableSettings.ajax);
    this.setState({ hasAjax });
  };

  render() {
    const { t, classes, width, logs } = this.props;
    const beginTimestamp = subHours(new Date(), HOURS_BEFORE).getTime();
    const ajaxData1 = logs.map(({ timestamp, eventCode, hubName, sourceObjectName, sourceRoomName }) => ({
      time: format(new Date(timestamp), 'd.M.yyyy HH:mm:ss'),
      timestamp,
      controller: `hub: ${hubName}`,
      tag: null,
      event: t(`ajax.${eventCode}`, { hubName, sourceObjectName, sourceRoomName }),
      person: null,
      controllerMessageType: 'normal',
    }));
    // filter the last 6h
    const ajaxData = ajaxData1.filter((entry) => entry.timestamp > beginTimestamp);
    const isLarge = width === 'lg' || width === 'xl';
    const rawReportData = cloneDeep(this.props.eventReport);

    const reportData = rawReportData.map((row) => {
      return {
        ...row,
        time: formatDateTime(hackToSubtractOffset(row.time)),
        timestamp: getTimestamp(hackToSubtractOffset(row.time)),
        person: row.person?.replaceAll('null', ''), //TODO remove once backend fixed (ticket #678 )
      };
    });
    const mergedDataWithAjax = this.props.includeAjax ? ajaxData.concat(reportData) : reportData;
    const webSocketEventsData = this.props.webSocketEvents.map((row) => {
      // controller time is in UNIX timestamp in format 1665240120000
      const formattedTime = formatDateTime(hackToSubtractOffset(new Date(row.time).toISOString()));
      return {
        ...row,
        time: formattedTime,
        timestamp: row.time,
        person: row.person?.replaceAll('null', ''), //TODO remove once backend fixed (ticket #678 )
      };
    });
    const mergedData = mergedDataWithAjax.concat(webSocketEventsData);
    const fullData = Immutable.asMutable(mergedData).sort((a, b) => b.timestamp - a.timestamp);
    const hasWarningsOrErrors =
      fullData.findIndex(
        ({ controllerMessageType }) =>
          controllerMessageType === 'warning' ||
          controllerMessageType === 'error' ||
          controllerMessageType === 'success',
      ) > -1;
    const rawSitesData = cloneDeep(this.props.sites);
    const sitesData = rawSitesData
      ? rawSitesData.map((site) => ({
          ...site,
          name: site.address.fullName,
        }))
      : null;

    const monitoringColumns = [
      { id: 'time', field: 'time', title: t('ReportManager_getEventReport_col2') },
      { id: 'controller', field: 'controller', title: t('ReportManager_getEventReport_col3') },
      { id: 'tag', field: 'tag', title: t('ReportManager_getEventReport_col4') },
      { id: 'event', field: 'event', title: t('ReportManager_getEventReport_col5') },
      { id: 'person', field: 'person', title: t('ReportManager_getEventReport_col6') },
    ];

    return (
      <MainContentContainer className={classes.MainContentContainer}>
        <AlertDialog
          open={this.state.isAlertDialogOpen}
          handleClose={() => this.setState({ isAlertDialogOpen: false })}
          title={this.state.alertTitle}
          contentText={this.state.alertContentText}
          cancelButtonText={t('Global_cancel')}
          confirmButtonText={t('Global_delete')}
          onConfirm={this.state.alertConfirmAction}
        />
        <SnackbarNotification
          variant={this.state.snackbarVariant}
          open={this.state.isSnackbarNotificationOpen}
          message={this.state.snackbarMessage}
          close={() => this.setState({ isSnackbarNotificationOpen: false })}
        />
        {/* <WebSocket
          subscribeSites={this.state.selectedSites.map(({ siteId }) => siteId)}
        /> */}
        <Modal
          open={this.state.isSitesModalOpen}
          handleClose={this.closeModalWithoutSave}
          Content={SelectSitesCheckAllModal}
          onClickSave={this.saveSelectedSites}
          selectAtLeastOneSiteError={this.state.selectAtLeastOneSiteError}
          saveButtonDisabled={this.state.selectAtLeastOneSiteError}
          showSelectAll
          // search sites table props
          checkboxColumn="start"
          data={sitesData}
          noDataText={t('controllerManager_noSite')}
          noHeader
          noFooter
          handleClick={(site) => this.selectSite(site)}
          columns={[{ title: t('common.name'), field: 'name', id: 'name' }]}
          activeRowField={'siteId'}
          checkedArray={this.state.tempSelectedSites}
          isSelectAllSitesButtonChecked={this.state.isSelectAllSitesButtonChecked}
          changeSelectAllSitesButton={() =>
            this.setState((prevState) => {
              const selectAtLeastOneSiteError = prevState.isSelectAllSitesButtonChecked;
              return {
                isSelectAllSitesButtonChecked: !prevState.isSelectAllSitesButtonChecked,
                selectAtLeastOneSiteError,
              };
            })
          }
        />
        <Header />
        <Grid container className={classes.gridContainer} spacing={isLarge ? 6 : 5}>
          <Grid item xs={12}>
            <Paper className={clsx(classes.paper, classes.reportWidget)}>
              <Table
                isMonitoring
                openSitesModal={() => this.setState({ isSitesModalOpen: true, tempSelectedSites: this.state.selectedSites })}
                hasAjax={this.state.hasAjax}
                includeAjax={this.props.includeAjax}
                onChangeIncludeAjax={(event) => this.props.setAjaxIncludedInMonitoring(event.target.checked)}
                noFooter
                data={fullData}
                noDataText={t('Global_noData')}
                columns={monitoringColumns}
                hasWarningsOrErrors={hasWarningsOrErrors}
                colorRowField={'controllerMessageType'}
                successCondition={'success'}
                warningCondition={'warning'}
                errorCondition={'error'}
                handleSearch={this.handleSearchMonitoring}
                sortBy={this.state.monitoringSortBy}
                sortDirection={this.state.monitoringSortDirection}
                handleSort={this.handleSortMonitoring}
                searchPlaceholder={t('ReportManager_getEventReport_search')}
                itemsPerPage={this.state.monitoringItemsPerPage}
                changeItemsPerPage={this.changeMonitoringItemsPerPage}
                totalElements={this.props.monitoringTotalElements}
                page={this.state.monitoringPageNumber}
                changePage={this.changeMonitoringPage}
                onKeyPress={this.handleSearchMonitoringKeyPress}
                // rows per page select props
                selectData={[5, 10, 20]}
                selectLabel={t('table.showPerPage')}
                selectValue={this.state.monitoringItemsPerPage}
                onSelect={this.changeMonitoringItemsPerPage}
                selectWidth={90}
                copyable
              />
            </Paper>
          </Grid>
        </Grid>
      </MainContentContainer>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    customerId: state.auth.customerId,
    customerIdSub: state.auth.customerIdSub,
    customers: state.customers.customers,
    // Ajax
    includeAjax: state.ajax.isAjaxIncludedInMonitoring,
    userId: state.ajax.userId,
    hubs: state.ajax.hubs,
    logs: state.ajax.logs,
    ajaxIsLoading: state.ajax.isLoading,
    ajaxSuccessMessage: state.ajax.successMessage,
    ajaxErrorMessage: state.ajax.errorMessage,
    // Event Reports
    eventReportIsLoading: state.eventReports.isLoading,
    eventReportErrorMessage: state.eventReports.errorMessage,
    eventReport: state.eventReports.eventReport,
    eventReportTotalElements: state.eventReports.eventReportTotalElements,
    // filters
    sites: state.sites.allSites,
    sitesIsLoading: state.sites.isLoading,
    // webSocket
    webSocketEvents: state.eventReports.webSocketEvents,
  };
};

const mapDispatchToProps = (dispatch) => ({
  // Ajax
  setAjaxIncludedInMonitoring: (includeAjax) => dispatch(AjaxActions.setAjaxIncludedInMonitoring(includeAjax)),
  loginAjax: (customerId) => dispatch(AjaxActions.loginAjaxRequest(customerId)),
  getHubs: (customerId, userId) => dispatch(AjaxActions.getHubsRequest(customerId, userId)),
  getLogs: (customerId, userId, hubId) => dispatch(AjaxActions.getLogsRequest(customerId, userId, hubId)),
  resetAjax: () => dispatch(AjaxActions.resetAjax()),
  // Event reports
  getEventReport: (customerId, filters, params) =>
    dispatch(EventReportsActions.getEventReportRequest(customerId, filters, params)),

  // filter data
  getAllSites: (customerId, pageParams) => dispatch(SitesActions.getAllSitesRequest(customerId, pageParams)),
  resetSites: () => dispatch(SitesActions.resetSites()),
  clearWebSocketEvents: () => dispatch(EventReportsActions.clearWebSocketEvents()),
  // Web socket sites subscription
  updateSitesQueues: (sites) => dispatch(WebSocketActions.updateSitesQueues(sites)),
});

export default compose(
  withTranslation(),
  withStyles(styles),
  withWidth(),
  connect(mapStateToProps, mapDispatchToProps),
)(Monitoring);
